import { useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Banner, Button, Chip, Field, Sheet, useToast } from '../ui'
import { useAddItem, useBumpUsual, useRemoveItem, useSetItemFields, useUpdateItem } from '../lib/mutations'
import { useGroceryAisles, useAisleDict } from '../lib/groceryAisles'
import { useWriteError } from '../lib/useAction'
import { addToList, type AddOutcome } from '../lib/listAdd'
import { placeholderFor, preselectList, quickAddTargets, readLastList, writeLastList } from '../lib/quickAdd'
import type { List, ListItem } from '../lib/types'
import { KindIcon } from './KindIcon'

/**
 * Lists P3: the header's "+". One line, one list, without opening the list.
 *
 * It adds through `lib/listAdd.ts`, the same functions the list's own screen
 * calls, so "Pay rent tomorrow" gets its due date, "2 kg potatoes" its
 * quantity (and the duplicate rule), and a pasted link its preview, exactly
 * as they would from inside the list. The sheet stays open after an add —
 * the field clears and keeps focus — because a second thing to add is the
 * usual case and reopening a sheet for it is the cost this exists to avoid.
 *
 * Mounted only while open, so the mutation hooks and the aisle query below
 * cost nothing on the Lists index the rest of the time.
 */
export function QuickAdd({ lists, items, onClose, onNewList }: {
  lists: List[]
  items: ListItem[]
  onClose: () => void
  /** Closes this sheet and opens "New list" (the caller does both, so there
   *  is one sheet at a time and no stacking). */
  onNewList: () => void
}) {
  const toast = useToast()
  const qc = useQueryClient()
  const addM = useAddItem()
  const updateM = useUpdateItem()
  const fieldsM = useSetItemFields()
  const bumpM = useBumpUsual()
  const removeM = useRemoveItem()
  const aislesQ = useGroceryAisles()
  const dict = useAisleDict()
  const error = useWriteError(addM, updateM, fieldsM, removeM)

  const targets = useMemo(() => quickAddTargets(lists), [lists])
  // The explicit choice; the preselect rule answers until there is one. Held
  // as an id rather than a list so a list archived on another device while
  // this is open falls back through the rule instead of adding to it.
  const [picked, setPicked] = useState<string | null>(null)
  const [initial] = useState(() => readLastList())
  const target = (picked ? targets.find(l => l.id === picked) : null) ?? preselectList(lists, initial)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const field = useRef<HTMLInputElement>(null)

  const itemsOf = (id: string) => items.filter(i => i.list_id === id)

  async function submit() {
    if (!target || busy || !draft.trim()) return
    setBusy(true)
    let out: AddOutcome
    try {
      out = await addToList(
        { addM, updateM, fieldsM, bumpM, qc, aisles: aislesQ.data ?? [], dict },
        target, itemsOf(target.id), draft,
      )
    } finally {
      setBusy(false)
    }
    if (!out.ok || out.empty) return
    writeLastList(target.id)
    setDraft('')
    field.current?.focus()

    const wrote = out.ids.length > 0 || out.bumped.length > 0
    if (!wrote && out.already.length) {
      toast({ text: `Already on the list: ${out.already.join(', ')}` })
      return
    }
    toast({
      text: `Added to ${target.name}`,
      action: { label: 'Undo', onClick: () => undo(out) },
    })
  }

  /** Takes back what this one add wrote: the rows it inserted, and the
   *  counts it raised on rows already there. */
  function undo(out: AddOutcome) {
    for (const id of out.ids) removeM.mutate(id)
    for (const b of out.bumped) updateM.mutate({ id: b.id, patch: { qty: b.was } })
  }

  return (
    <Sheet
      open onOpenChange={o => { if (!o) onClose() }} title="Add" closeButton
      actions={
        <>
          <Button variant="primary" block busy={busy} disabled={!target || !draft.trim()} onClick={() => void submit()}>
            Add
          </Button>
          <Button variant="quiet" onClick={onNewList}>New list…</Button>
        </>
      }
    >
      <div className="lists-form quickadd">
        {error && <Banner tone="bad">{error}</Banner>}
        {target ? (
          <>
            <Field label="Item">
              <Field.Input
                ref={field} autoFocus value={draft} autoComplete="off" enterKeyHint="done"
                placeholder={placeholderFor(target.kind, target.name)}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') void submit() }}
              />
            </Field>
            <div className="quickadd__to">
              <p className="quickadd__label" id="quickadd-to">Add to</p>
              <div className="quickadd__chips" role="group" aria-labelledby="quickadd-to">
                {targets.map(l => (
                  <Chip key={l.id} on={l.id === target.id} className="quickadd__chip" onClick={() => { setPicked(l.id); field.current?.focus() }}>
                    <KindIcon kind={l.kind} size={16} />
                    {l.name}
                  </Chip>
                ))}
              </div>
            </div>
          </>
        ) : (
          <p className="quickadd__none">No lists yet. Start one to add to it.</p>
        )}
      </div>
    </Sheet>
  )
}
